import { Show } from "solid-js";
import { Title } from "@solidjs/meta";
import { Banner } from "~/common/banner";
import { Button } from "~/common/button";
import { Form, InputField } from "~/common/form";
import { useForm } from "~/inertia";
import { newUserPasswordPath, newUserSessionPath, userConfirmationPath } from "~/routes";
import { AuthPageLayout, AuthPageLink } from "./_layout";

type Props = {
  sent: boolean;
  strings: {
    title: string;
    emailLabel: string;
    submit: string;
    sentTitle: string;
    sentBody: string;
    backToSignIn: string;
    forgotPassword: string;
  };
};

export default function ResendConfirmation(props: Props) {
  const form = useForm({ email: "" }).transform((data) => ({ user: data }));
  // Set when a confirmation link from an email turns out invalid/expired —
  // the confirmations#show failure redirects here with these errors.
  const tokenErrors = () =>
    (form.errors as Record<string, string[] | undefined>)["confirmationToken"];

  return (
    <AuthPageLayout>
      <Title>{props.strings.title}</Title>

      <Show
        when={!props.sent}
        fallback={
          <div>
            <h1 class="text-2xl font-semibold">{props.strings.sentTitle}</h1>
            <p class="mt-2 text-typography-secondary">{props.strings.sentBody}</p>
          </div>
        }
      >
        <h1 class="text-2xl font-semibold">{props.strings.title}</h1>

        <Form
          class="space-y-6"
          onSubmit={(e) => {
            e.preventDefault();
            form.post(userConfirmationPath());
          }}
        >
          <Show when={tokenErrors()?.length}>
            <Banner variant="error" description={tokenErrors()!.join(", ")} />
          </Show>

          <InputField
            label={props.strings.emailLabel}
            type="email"
            required
            autofocus
            autocomplete="email"
            value={form.data.email}
            errors={form.errors.email}
            onChange={(e) => form.setData("email", e.target.value)}
          />

          <Button
            type="submit"
            variant="primary"
            size="lg"
            width="full"
            class="mt-12"
            loading={form.processing}
          >
            {props.strings.submit}
          </Button>
        </Form>
      </Show>

      <div class="space-y-2">
        <AuthPageLink href={newUserSessionPath()}>{props.strings.backToSignIn}</AuthPageLink>
        <AuthPageLink href={newUserPasswordPath()}>{props.strings.forgotPassword}</AuthPageLink>
      </div>
    </AuthPageLayout>
  );
}
